/**
 * Navigation for both workspaces, and the breadcrumb resolver.
 *
 * The app has two sidebars, not one. `PLATFORM_NAV` is global operator work;
 * `ORG_NAV` is scoped to a single tenant and its paths are built from an
 * `orgId`. Which one renders is decided by the route, so a URL alone is enough
 * to place you — no context needs restoring on refresh.
 *
 * Groups are named for what the operator is doing, not for who owns the
 * record. The previous Platform/Partner/Client/Security split meant a single
 * job — "this tenant's tasks are failing" — spanned four of them.
 */

export interface NavItem {
  to: string;
  label: string;
  /** Material Symbols ligature. */
  icon: string;
}

export interface NavGroup {
  /** Ungrouped when absent — the top block renders without a heading. */
  label?: string;
  items: NavItem[];
}

/**
 * Twelve destinations. The review queue is rows on the Overview board and
 * trigger templates are a tab of Plugins, so neither is a place of its own
 * (Feature 70). Settings, a tab of Tasks from Feature 70, is a destination
 * again under Runtime (Feature 87, D3). Linkages sit beside
 * Plugins in Catalog: a chain of capabilities is drawn, not installed, so it
 * is a destination of its own rather than a Plugins tab (Feature 71 D31).
 */
export const PLATFORM_NAV: readonly NavGroup[] = [
  {
    items: [{ to: '/overview', label: 'Overview', icon: 'space_dashboard' }],
  },
  {
    label: 'Tenants',
    items: [
      { to: '/organizations', label: 'Organizations', icon: 'apartment' },
      { to: '/accounts', label: 'Accounts', icon: 'person' },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { to: '/plugins', label: 'Plugins', icon: 'extension' },
      { to: '/automation', label: 'Automation', icon: 'automation' },
    ],
  },
  {
    label: 'Runtime',
    items: [
      { to: '/tasks', label: 'Tasks', icon: 'task_alt' },
      { to: '/cost', label: 'Cost', icon: 'payments' },
      { to: '/sandbox', label: 'Sandbox', icon: 'science' },
      // Its own page again since Feature 87 (D3); it was a tab of Tasks.
      { to: '/settings', label: 'Settings', icon: 'settings' },
    ],
  },
  {
    label: 'Access',
    items: [
      // Staff is platform-scoped: it answers "who operates this", which is an
      // access question, not a tenant one. It sits with Federation and
      // Security for that reason, not under Tenants next to Accounts.
      { to: '/staff', label: 'Staff', icon: 'badge' },
      { to: '/federation', label: 'Federation', icon: 'hub' },
      { to: '/security', label: 'Security', icon: 'shield' },
    ],
  },
];

export function orgNav(orgId: string): readonly NavGroup[] {
  const base = `/orgs/${orgId}`;
  return [
    {
      items: [
        { to: `${base}/overview`, label: 'Overview', icon: 'space_dashboard' },
        { to: `${base}/agents`, label: 'Agents', icon: 'smart_toy' },
        { to: `${base}/collections`, label: 'Collections', icon: 'folder_open' },
        // Who belongs to the tenant, beside Groups, which says what they may read.
        { to: `${base}/users`, label: 'Users', icon: 'group' },
        // Groups, not "Members & groups": after Feature 32 a group is the only
        // ACL grant, which makes it the join between the people who may ask and
        // the data they may reach. A member list with a groups column buries it.
        { to: `${base}/groups`, label: 'Groups', icon: 'shield_person' },
      ],
    },
    {
      label: 'Configuration',
      items: [
        { to: `${base}/config`, label: 'Capabilities', icon: 'settings' },
        { to: `${base}/secrets`, label: 'Secrets', icon: 'key' },
        // The organization's linkages (Feature 74 D6, Feature 77 D29).
        { to: `${base}/automation`, label: 'Automation', icon: 'automation' },
      ],
    },
    {
      label: 'Activity',
      items: [
        { to: `${base}/tasks`, label: 'Tasks', icon: 'task_alt' },
        { to: `${base}/cost`, label: 'Cost', icon: 'payments' },
        // Onboarding is a card on the tenant overview until its checklist is
        // complete, not a destination (Feature 70).
      ],
    },
  ];
}

/** Detail routes that are not nav entries but still need a breadcrumb parent. */
const DETAIL_PARENTS: Record<string, { to: string; label: string }> = {
  '/accounts/': { to: '/accounts', label: 'Accounts' },
  '/plugins/': { to: '/plugins', label: 'Plugins' },
  '/automation/': { to: '/automation', label: 'Automation' },
  '/tasks/': { to: '/tasks', label: 'Tasks' },
  '/federation/partners/': { to: '/federation', label: 'Federation' },
};

export interface Crumb {
  label: string;
  /** Absent on the final crumb — you are already there. */
  to?: string;
}

/**
 * Resolve a pathname to its breadcrumb trail.
 *
 * Inside a tenant the trail always starts at Platform, so there is a way back
 * out from the header as well as from the sidebar.
 */
export function resolveCrumbs(pathname: string, orgName?: string): Crumb[] {
  const orgMatch = /^\/orgs\/([^/]+)(\/.*)?$/.exec(pathname);

  if (orgMatch) {
    const [, orgId, rest = ''] = orgMatch;
    const trail: Crumb[] = [
      { label: 'Platform', to: '/overview' },
      { label: orgName ?? 'Organization', to: `/orgs/${orgId}/overview` },
    ];
    const leaf = orgNav(orgId ?? '')
      .flatMap((g) => g.items)
      .find((i) => i.to === pathname);
    if (leaf && rest !== '/overview') trail.push({ label: leaf.label });
    return trail;
  }

  const item = PLATFORM_NAV.flatMap((g) => g.items).find((i) => i.to === pathname);
  if (item) {
    return item.to === '/overview'
      ? [{ label: 'Platform' }]
      : [{ label: 'Platform', to: '/overview' }, { label: item.label }];
  }

  for (const [prefix, parent] of Object.entries(DETAIL_PARENTS)) {
    if (pathname.startsWith(prefix) && pathname.length > prefix.length) {
      return [
        { label: 'Platform', to: '/overview' },
        { label: parent.label, to: parent.to },
      ];
    }
  }

  return [{ label: 'Platform', to: '/overview' }];
}

/**
 * The nav group a route belongs to, for the page header's eyebrow.
 *
 * Ungrouped nav blocks have no label of their own, so they take the name of
 * the workspace — the eyebrow answers "where am I", and for Overview or Staff
 * the honest answer is "Platform".
 */
export function resolveSection(pathname: string): string {
  const orgMatch = /^\/orgs\/([^/]+)(\/.*)?$/.exec(pathname);
  if (orgMatch) {
    const [, orgId] = orgMatch;
    for (const group of orgNav(orgId ?? '')) {
      if (group.items.some((i) => pathname.startsWith(i.to))) {
        return group.label ?? 'Organization';
      }
    }
    return 'Organization';
  }

  for (const group of PLATFORM_NAV) {
    if (group.items.some((i) => pathname === i.to)) return group.label ?? 'Platform';
  }

  for (const [prefix, parent] of Object.entries(DETAIL_PARENTS)) {
    if (pathname.startsWith(prefix) && pathname.length > prefix.length) {
      const group = PLATFORM_NAV.find((g) => g.items.some((i) => i.to === parent.to));
      return group?.label ?? 'Platform';
    }
  }

  return 'Platform';
}
